import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Mail,
  Send,
  X,
  Bell,
  Check,
  Trash2,
  Edit,
  Play,
  RefreshCw,
  CalendarDays,
  Timer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function EmailScheduler() {
  const [emails, setEmails] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduler, setShowScheduler] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    recipient_email: "",
    recipient_name: "",
    client_id: "",
    subject: "",
    body: "",
    scheduled_for: "",
    reminder_time: 30
  });

  useEffect(() => {
    fetchEmails();
    fetchClients();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await api.get("/scheduled-emails");
      setEmails(res.data.emails || []);
    } catch (e) {
      console.error("Error fetching scheduled emails:", e);
      toast.error("Failed to load scheduled emails");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data || []);
    } catch (e) {
      console.error("Error fetching clients:", e);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: clientId,
        recipient_email: client.email || "",
        recipient_name: client.name || ""
      });
    }
  };

  const openScheduler = (email = null) => {
    if (email) {
      setEditingEmail(email);
      const scheduledDate = new Date(email.scheduled_for);
      setFormData({
        recipient_email: email.recipient_email || "",
        recipient_name: email.recipient_name || "",
        client_id: email.client_id || "",
        subject: email.subject || "",
        body: email.body || "",
        scheduled_for: scheduledDate.toISOString().slice(0, 16),
        reminder_time: email.reminder_time || 30
      });
    } else {
      setEditingEmail(null);
      // Default to tomorrow 9 AM
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setFormData({
        recipient_email: "",
        recipient_name: "",
        client_id: "",
        subject: "",
        body: "",
        scheduled_for: tomorrow.toISOString().slice(0, 16),
        reminder_time: 30
      });
    }
    setShowScheduler(true);
  };

  const handleSave = async () => {
    if (!formData.subject.trim() || !formData.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (!formData.scheduled_for) {
      toast.error("Please select a date and time");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        scheduled_for: new Date(formData.scheduled_for).toISOString()
      };

      if (editingEmail) {
        await api.put(`/scheduled-emails/${editingEmail.id}`, payload);
        toast.success("Email updated!");
      } else {
        await api.post("/scheduled-emails", payload);
        toast.success("Email scheduled!");
      }
      setShowScheduler(false);
      fetchEmails();
    } catch (e) {
      console.error("Error saving email:", e);
      toast.error(e.response?.data?.detail || "Failed to schedule email");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (emailId) => {
    if (!confirm("Cancel this scheduled email?")) return;
    try {
      await api.post(`/scheduled-emails/${emailId}/cancel`);
      toast.success("Email cancelled");
      fetchEmails();
    } catch (e) {
      toast.error("Failed to cancel email");
    }
  };

  const handleSendNow = async (emailId) => {
    try {
      await api.post(`/scheduled-emails/${emailId}/send-now`);
      toast.success("Email marked as sent! Copy the content to send via your email client.");
      fetchEmails();
    } catch (e) {
      toast.error("Failed to send email");
    }
  };

  const handleDelete = async (emailId) => {
    if (!confirm("Delete this email?")) return;
    try {
      await api.delete(`/scheduled-emails/${emailId}`);
      toast.success("Email deleted");
      fetchEmails();
    } catch (e) {
      toast.error("Failed to delete email");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-500/20 text-indigo-400 border-slate-400/30",
      sent: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-slate-500/20 text-indigo-400 border-slate-400/30",
      failed: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return styles[status] || styles.scheduled;
  };

  const upcomingEmails = emails.filter(e => e.status === "scheduled");
  const pastEmails = emails.filter(e => e.status !== "scheduled");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-scheduler">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-indigo-400" />
            Email Scheduler
          </h1>
          <p className="text-indigo-400">Schedule emails to send later with reminders</p>
        </div>
        <button
          onClick={() => openScheduler()}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors"
          data-testid="schedule-email-btn"
        >
          <Clock className="w-4 h-4" />
          Schedule Email
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Timer className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">{upcomingEmails.length}</p>
                <p className="text-sm text-indigo-400">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">
                  {emails.filter(e => e.status === "sent").length}
                </p>
                <p className="text-sm text-indigo-400">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-500/20 rounded-xl">
                <Bell className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-500">
                  {upcomingEmails.filter(e => !e.reminder_sent).length}
                </p>
                <p className="text-sm text-indigo-400">Reminders Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Emails */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-400" />
            Upcoming Scheduled Emails
          </CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingEmails.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-indigo-400">No scheduled emails</p>
              <button
                onClick={() => openScheduler()}
                className="mt-3 text-indigo-400 hover:text-indigo-400"
              >
                Schedule your first email →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingEmails.map((email) => (
                <div
                  key={email.id}
                  className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-indigo-500 truncate">{email.subject}</h4>
                        <Badge className={getStatusBadge(email.status)}>
                          {email.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-indigo-400 truncate">
                        To: {email.recipient_name || email.recipient_email || "No recipient"}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(email.scheduled_for)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bell className="w-3 h-3" />
                          {email.reminder_time}min reminder
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openScheduler(email)}
                        className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-400 rounded-lg"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSendNow(email.id)}
                        className="p-2 text-indigo-400 hover:text-green-400 hover:bg-slate-400 rounded-lg"
                        title="Send Now"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCancel(email.id)}
                        className="p-2 text-indigo-400 hover:text-red-400 hover:bg-slate-400 rounded-lg"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Emails */}
      {pastEmails.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-indigo-500 text-lg">History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pastEmails.slice(0, 10).map((email) => (
                <div
                  key={email.id}
                  className="flex items-center justify-between p-3 bg-slate-900/30 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-400 truncate">{email.subject}</p>
                    <p className="text-xs text-slate-400">
                      {formatDate(email.sent_at || email.cancelled_at || email.created_at)}
                    </p>
                  </div>
                  <Badge className={getStatusBadge(email.status)}>
                    {email.status}
                  </Badge>
                  <button
                    onClick={() => handleDelete(email.id)}
                    className="p-1.5 ml-2 text-slate-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schedule Email Modal */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">
              {editingEmail ? "Edit Scheduled Email" : "Schedule Email"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recipient */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Select Client</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={handleClientSelect}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue placeholder="Choose client..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id} className="text-indigo-500">
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400">Recipient Email</Label>
                <Input
                  value={formData.recipient_email}
                  onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <Label className="text-slate-400">Subject</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Email subject..."
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                data-testid="scheduled-email-subject"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-slate-400">Email Body</Label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Write your email..."
                rows={6}
                className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                data-testid="scheduled-email-body"
              />
            </div>

            {/* Schedule Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400">Schedule For</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_for}
                  onChange={(e) => setFormData({ ...formData, scheduled_for: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-indigo-500 mt-1"
                  data-testid="scheduled-email-datetime"
                />
              </div>
              <div>
                <Label className="text-slate-400">Remind Me (minutes before)</Label>
                <Select
                  value={String(formData.reminder_time)}
                  onValueChange={(v) => setFormData({ ...formData, reminder_time: parseInt(v) })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="15" className="text-indigo-500">15 minutes</SelectItem>
                    <SelectItem value="30" className="text-indigo-500">30 minutes</SelectItem>
                    <SelectItem value="60" className="text-indigo-500">1 hour</SelectItem>
                    <SelectItem value="120" className="text-indigo-500">2 hours</SelectItem>
                    <SelectItem value="1440" className="text-indigo-500">1 day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <button
              onClick={() => setShowScheduler(false)}
              className="px-4 py-2 text-indigo-400 hover:text-indigo-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg transition-colors"
              data-testid="save-scheduled-email-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Clock className="w-4 h-4" />
              )}
              {editingEmail ? "Update" : "Schedule"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
